/**
 * Granular Agent Permissions System
 * Fine-grained permissions that can be toggled by role
 */

export type AgentPermission =
  // Customer & PII
  | 'view_customer_pii'
  | 'edit_customer'
  | 'export_customer_data'
  
  // Case Management
  | 'view_case'
  | 'edit_case'
  | 'create_case'
  | 'close_case'
  | 'reopen_case'
  | 'reassign_case'
  | 'merge_cases'
  | 'delete_case'
  
  // Notes & Comments
  | 'add_note'
  | 'edit_own_note'
  | 'edit_any_note'
  | 'view_internal_notes'
  
  // Attachments
  | 'upload_attachment'
  | 'download_attachment'
  | 'delete_attachment'
  
  // Voice
  | 'use_dialpad'
  | 'make_outbound_call'
  | 'transfer_call'
  | 'conference_call'
  | 'record_call'
  | 'listen_recording'
  | 'download_recording'
  
  // Digital Channels
  | 'send_email'
  | 'send_chat'
  | 'send_sms'
  | 'send_whatsapp'
  
  // Quality
  | 'view_quality_scores'
  | 'view_own_evaluations'
  | 'acknowledge_coaching'
  | 'submit_self_evaluation'
  
  // Workforce
  | 'view_schedule'
  | 'request_pto'
  | 'request_shift_swap'
  | 'clock_in_out'
  
  // Knowledge Base
  | 'view_kb_articles'
  | 'suggest_kb_article'
  
  // Templates
  | 'use_templates'
  | 'create_personal_template'
  
  | 'view_team_stats'
  // Admin Actions (typically supervisor+)
  | 'view_realtime_dashboard'
  | 'monitor_agents'
  | 'barge_call'
  | 'whisper_call'
  | 'force_logout_agent';

// Default permission sets by role
// IMPORTANT: New agents start with MINIMAL permissions
// Baseline: Schedule + Clock + VIEW tickets (no PII)
// Administrators grant additional permissions individually
export const AGENT_PERMISSION_SETS: Record<string, AgentPermission[]> = {
  // New agents get Schedule + Clock + View Tickets (without PII)
  // All other permissions must be granted by admin
  agent: [
    'view_schedule',
    'clock_in_out',
    'view_case',           // Can see assigned tickets
    'add_note',            // Can add notes to tickets
    'view_kb_articles',    // Can search knowledge base
  ],
  
  // Grantable permissions for agents - admins can grant any of these
  grantable_agent_permissions: [
    // Customer & PII
    'view_customer_pii',
    'edit_customer',
    
    // Case Management
    'view_case',
    'edit_case',
    'create_case',
    'close_case',
    'reopen_case',
    'reassign_case',
    
    // Notes
    'add_note',
    'edit_own_note',
    'view_internal_notes',
    
    // Attachments
    'upload_attachment',
    'download_attachment',
    
    // Voice
    'use_dialpad',
    'make_outbound_call',
    'transfer_call',
    'conference_call',
    'record_call',
    'listen_recording',
    
    // Digital
    'send_email',
    'send_chat',
    'send_sms',
    'send_whatsapp',
    
    // Quality
    'view_quality_scores',
    'view_own_evaluations',
    'acknowledge_coaching',
    'submit_self_evaluation',
    
    // Workforce
    'request_pto',
    'request_shift_swap',
    
    // KB
    'view_kb_articles',
    'suggest_kb_article',
    
    // Templates
    'use_templates',
    'create_personal_template'
  ],
  
  senior_agent: [
    // Base minimal + additional
    'view_schedule',
    'clock_in_out',
    'view_customer_pii',
    'view_case',
    'edit_case',
    'create_case',
    'close_case',
    'reassign_case',
    'add_note',
    'edit_own_note',
    'view_internal_notes',
    'upload_attachment',
    'download_attachment',
    'use_dialpad',
    'make_outbound_call',
    'transfer_call',
    'conference_call',
    'record_call',
    'listen_recording',
    'send_email',
    'send_chat',
    'send_sms',
    'send_whatsapp',
    'view_quality_scores',
    'view_own_evaluations',
    'acknowledge_coaching',
    'request_pto',
    'request_shift_swap',
    'view_kb_articles',
    'suggest_kb_article',
    'use_templates',
    'create_personal_template'
  ],
  
  team_leader: [
    // All senior + supervision
    'view_schedule',
    'clock_in_out',
    'view_customer_pii',
    'edit_customer',
    'view_case',
    'edit_case',
    'create_case',
    'close_case',
    'reopen_case',
    'reassign_case',
    'merge_cases',
    'add_note',
    'edit_own_note',
    'edit_any_note',
    'view_internal_notes',
    'upload_attachment',
    'download_attachment',
    'delete_attachment',
    'use_dialpad',
    'make_outbound_call',
    'transfer_call',
    'conference_call',
    'record_call',
    'listen_recording',
    'download_recording',
    'send_email',
    'send_chat',
    'send_sms',
    'send_whatsapp',
    'view_quality_scores',
    'view_own_evaluations',
    'acknowledge_coaching',
    'submit_self_evaluation',
    'request_pto',
    'request_shift_swap',
    'view_kb_articles',
    'suggest_kb_article',
    'use_templates',
    'create_personal_template',
    'view_team_stats',
    'view_realtime_dashboard',
    'monitor_agents',
    'whisper_call'
  ],
  
  supervisor: [
    // All permissions
    'view_customer_pii',
    'edit_customer',
    'export_customer_data',
    'view_case',
    'edit_case',
    'create_case',
    'close_case',
    'reopen_case',
    'reassign_case',
    'merge_cases',
    'delete_case',
    'add_note',
    'edit_own_note',
    'edit_any_note',
    'view_internal_notes',
    'upload_attachment',
    'download_attachment',
    'delete_attachment',
    'use_dialpad',
    'make_outbound_call',
    'transfer_call',
    'conference_call',
    'record_call',
    'listen_recording',
    'download_recording',
    'send_email',
    'send_chat',
    'send_sms',
    'send_whatsapp',
    'view_quality_scores',
    'view_own_evaluations',
    'acknowledge_coaching',
    'submit_self_evaluation',
    'view_schedule',
    'request_pto',
    'request_shift_swap',
    'clock_in_out',
    'view_kb_articles',
    'suggest_kb_article',
    'use_templates',
    'create_personal_template',
    'view_team_stats',
    'view_realtime_dashboard',
    'monitor_agents',
    'barge_call',
    'whisper_call',
    'force_logout_agent'
  ],
};

// All grantable permissions for agents (used by admin UI)
export const ALL_GRANTABLE_PERMISSIONS: AgentPermission[] = 
  AGENT_PERMISSION_SETS.grantable_agent_permissions;

// Check if a role has a specific permission
// Now also checks custom permissions from database
export function hasAgentPermission(
  role: string | null,
  permission: AgentPermission,
  customPermissions?: AgentPermission[]
): boolean {
  // Check role-based permissions
  if (!role) return false;
  
  // Supervisors and above have all permissions
  if (role === 'supervisor' || role === 'admin' || role === 'super_admin') {
    return true;
  }
  
  // For agents, only give base minimal permissions if no custom permissions set
  const rolePermissions = AGENT_PERMISSION_SETS[role];
  if (!rolePermissions) return false;

  // Agents: custom permissions are *additive* (never remove base schedule/clock)
  if (role === 'agent' && customPermissions && customPermissions.length > 0) {
    return rolePermissions.includes(permission) || customPermissions.includes(permission);
  }

  return rolePermissions.includes(permission);
}

// Get all permissions for a role (base + custom)
export function getAgentPermissions(
  role: string | null, 
  customPermissions?: AgentPermission[]
): AgentPermission[] {
  if (!role) return [];
  
  if (role === 'supervisor' || role === 'admin' || role === 'super_admin') {
    // Return all permissions
    return AGENT_PERMISSION_SETS.supervisor;
  }
  
  // For agents, return custom permissions if they exist, otherwise base minimal
  if (role === 'agent' && customPermissions && customPermissions.length > 0) {
    // Merge base permissions with custom granted permissions
    const basePermissions = AGENT_PERMISSION_SETS.agent;
    return [...new Set([...basePermissions, ...customPermissions])];
  }
  
  return AGENT_PERMISSION_SETS[role] || [];
}

// Permission groups for UI display
export const PERMISSION_GROUPS = [
  {
    id: 'customer',
    name: 'Customer & PII',
    permissions: ['view_customer_pii', 'edit_customer', 'export_customer_data'],
  },
  {
    id: 'cases',
    name: 'Case Management',
    permissions: ['view_case', 'edit_case', 'create_case', 'close_case', 'reopen_case', 'reassign_case', 'merge_cases', 'delete_case'],
  },
  {
    id: 'notes',
    name: 'Notes & Comments',
    permissions: ['add_note', 'edit_own_note', 'edit_any_note', 'view_internal_notes'],
  },
  {
    id: 'attachments',
    name: 'Attachments',
    permissions: ['upload_attachment', 'download_attachment', 'delete_attachment'],
  },
  {
    id: 'voice',
    name: 'Voice',
    permissions: ['use_dialpad', 'make_outbound_call', 'transfer_call', 'conference_call', 'record_call', 'listen_recording', 'download_recording'],
  },
  {
    id: 'digital',
    name: 'Digital Channels',
    permissions: ['send_email', 'send_chat', 'send_sms', 'send_whatsapp'],
  },
  {
    id: 'quality',
    name: 'Quality',
    permissions: ['view_quality_scores', 'view_own_evaluations', 'acknowledge_coaching', 'submit_self_evaluation'],
  },
  {
    id: 'workforce',
    name: 'Workforce',
    permissions: ['view_schedule', 'request_pto', 'request_shift_swap', 'clock_in_out'],
  },
  {
    id: 'kb',
    name: 'Knowledge Base',
    permissions: ['view_kb_articles', 'suggest_kb_article'],
  },
  {
    id: 'templates',
    name: 'Templates',
    permissions: ['use_templates', 'create_personal_template'],
  },
  {
    id: 'admin',
    name: 'Supervisor Actions',
    permissions: ['view_team_stats', 'view_realtime_dashboard', 'monitor_agents', 'barge_call', 'whisper_call', 'force_logout_agent'],
  }
];
